import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, MagnifyingGlass, BookOpen, Calendar, ChartLine } from "@phosphor-icons/react";
import axios from "axios";
import { DEFAULT_JOURNALS } from "@/lib/mockData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

export default function JournalsPage() {
  const [journals, setJournals] = useState(DEFAULT_JOURNALS);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const fetchJournals = async () => {
      try {
        const response = await axios.get(`${API}/journals`);
        if (Array.isArray(response.data) && response.data.length > 0) {
          setJournals(response.data);
        } else {
          setJournals(DEFAULT_JOURNALS);
        }
      } catch (error) {
        console.error("Failed to fetch journals:", error);
        setJournals(DEFAULT_JOURNALS);
      } finally {
        setLoading(false);
      }
    };
    fetchJournals();
  }, []);

  const filteredJournals = journals.filter(journal =>
    journal.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    journal.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div data-testid="journals-page">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-[#0F172A] overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0D9488]/30 to-[#1E40AF]/30" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="text-[#0D9488] text-xs tracking-[0.2em] uppercase font-bold">
              Our Publications
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white font-['Chivo'] tracking-tight leading-none mt-4 mb-6">
              Scientific Journals
            </h1>
            <p className="text-lg text-[#CBD5E1] leading-relaxed">
              Explore our diverse portfolio of peer-reviewed journals covering medical, 
              pharmaceutical, and life sciences research.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Search Section */}
      <section className="py-8 bg-[#F8FAFC] border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative max-w-md">
            <MagnifyingGlass 
              size={20} 
              className="absolute left-4 top-1/2 -translate-y-1/2 text-[#64748B]" 
            />
            <input
              type="text"
              data-testid="journal-search-input"
              placeholder="Search journals..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full border border-[#CBD5E1] rounded-sm pl-12 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#0D9488] focus:border-transparent transition-all bg-white text-[#0F172A]"
            />
          </div>
        </div>
      </section>

      {/* Journals Grid */}
      <section data-testid="journals-grid-section" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid md:grid-cols-2 gap-8">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="bg-[#E2E8F0] h-48 rounded-sm mb-4" />
                  <div className="bg-[#E2E8F0] h-6 rounded-sm mb-2 w-3/4" />
                  <div className="bg-[#E2E8F0] h-4 rounded-sm w-full" />
                </div>
              ))}
            </div>
          ) : (
            <>
              {filteredJournals.length === 0 ? (
                <div className="text-center py-12">
                  <BookOpen size={48} className="text-[#CBD5E1] mx-auto mb-4" />
                  <p className="text-[#64748B]">No journals found matching your search.</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-8">
                  {filteredJournals.map((journal, index) => (
                    <motion.article
                      key={journal.id}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      data-testid={`journal-card-${journal.id}`}
                      className="group bg-white border border-[#E2E8F0] rounded-sm overflow-hidden hover:shadow-xl transition-all duration-300"
                    >
                      <div className="md:flex">
                        <div className="md:w-2/5 relative overflow-hidden">
                          <img
                            src={journal.image_url}
                            alt={journal.title}
                            className="w-full h-48 md:h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/60 to-transparent md:bg-gradient-to-r" />
                        </div>
                        <div className="md:w-3/5 p-6 md:p-8">
                          <h3 className="font-bold text-xl text-[#0F172A] font-['Chivo'] mb-3 group-hover:text-[#0D9488] transition-colors">
                            {journal.title}
                          </h3>
                          <p className="text-[#64748B] text-sm leading-relaxed mb-6">
                            {journal.description}
                          </p>
                          
                          <div className="grid grid-cols-2 gap-4 mb-6">
                            <div className="text-center p-3 bg-[#F8FAFC] rounded-sm">
                              <Calendar size={18} className="text-[#0D9488] mx-auto mb-1" />
                              <div className="text-xs text-[#64748B]">Frequency</div>
                              <div className="text-sm font-semibold text-[#0F172A]">{journal.frequency}</div>
                            </div>
                            <div className="text-center p-3 bg-[#F8FAFC] rounded-sm">
                              <ChartLine size={18} className="text-[#0D9488] mx-auto mb-1" />
                              <div className="text-xs text-[#64748B]">Impact</div>
                              <div className="text-sm font-semibold text-[#0F172A]">{journal.impact_factor}</div>
                            </div>
                          </div>

                          <div className="flex gap-3">
                            <Link
                              to="/submit-manuscript"
                              data-testid={`submit-to-${journal.id}`}
                              className="flex-1 inline-flex items-center justify-center gap-2 bg-[#0D9488] hover:bg-[#0F766E] text-white px-4 py-2 rounded-sm text-sm font-medium transition-colors"
                            >
                              Submit
                              <ArrowRight size={16} weight="bold" />
                            </Link>
                            <Link
                              to={`/journals/${journal.id}`}
                              data-testid={`view-${journal.id}`}
                              className="flex-1 inline-flex items-center justify-center gap-2 border border-[#0D9488] text-[#0D9488] hover:bg-[#F0FDFA] px-4 py-2 rounded-sm text-sm font-medium transition-colors"
                            >
                              View Details
                            </Link>
                          </div>
                        </div>
                      </div>
                    </motion.article>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Indexing Section */}
      <section data-testid="indexing-section" className="py-16 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-[#0D9488] text-xs tracking-[0.2em] uppercase font-bold">
              Global Reach
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#0F172A] font-['Chivo'] tracking-tight mt-4">
              Indexed & Abstracted In
            </h2>
          </div>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            {["PubMed", "Scopus", "Web of Science", "DOAJ", "CrossRef"].map((index) => (
              <div key={index} className="text-[#64748B] font-semibold text-lg">
                {index}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-24 bg-gradient-to-r from-[#0D9488] to-[#1E40AF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white font-['Chivo'] tracking-tight mb-4">
              Ready to Publish Your Research?
            </h2>
            <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
              Submit your manuscript to one of our prestigious journals and reach a global audience.
            </p>
            <Link
              to="/submit-manuscript"
              data-testid="journals-cta-submit"
              className="inline-flex items-center gap-2 bg-white text-[#0D9488] hover:bg-[#F0FDFA] px-8 py-4 rounded-sm font-semibold transition-colors"
            >
              Submit Your Manuscript
              <ArrowRight size={20} weight="bold" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
